import { Alert, Badge, Group, Skeleton, Stack, Table, Text, ActionIcon, Tooltip } from '@mantine/core';
import { IconExternalLink, IconDownload, IconAlertCircle } from '@tabler/icons-react';
import { DateTime } from 'luxon';
import { useInvoices } from '../../hooks/useInvoices';
import { AccountApi } from '../../AccountApi';
import { INVOICE_STATUS_COLORS } from '@/lib/constants/account';

export function InvoicesTab() {
  const { invoices, isLoading, isError } = useInvoices();

  if (isLoading) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} height={40} radius="sm" />
        ))}
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />}>
        Failed to load invoices. Please try again later.
      </Alert>
    );
  }

  if (invoices.length === 0) {
    return (
      <Text c="dimmed" size="sm" ta="center" py="xl">
        No invoices yet
      </Text>
    );
  }

  return (
    <Stack gap="md">
      <Table highlightOnHover aria-label="Invoices">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Invoice #</Table.Th>
            <Table.Th>Date Due</Table.Th>
            <Table.Th>Amount</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th ta="right">Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {invoices.map(invoice => {
            const viewUrl = AccountApi.createInvoiceUrl(invoice.invoiceId, invoice.viewSignature, false);
            const downloadUrl = AccountApi.createInvoiceUrl(invoice.invoiceId, invoice.viewSignature, true);
            const statusColor = INVOICE_STATUS_COLORS[invoice.invoiceStatusLabel ?? ''] ?? 'gray';

            return (
              <Table.Tr key={invoice.invoiceId}>
                <Table.Td>
                  <Text fw={500} size="sm">{invoice.invoiceNumber}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {DateTime.fromSeconds(invoice.invoiceDateDue).toLocaleString(DateTime.DATE_MED)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    ${invoice.total.toFixed(2)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={statusColor} variant="light" size="sm">
                    {invoice.invoiceStatusLabel ?? 'Unknown'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" justify="flex-end">
                    <Tooltip label="View invoice">
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        component="a"
                        href={viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`View invoice ${invoice.invoiceNumber}`}
                      >
                        <IconExternalLink size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Download PDF">
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        component="a"
                        href={downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Download invoice ${invoice.invoiceNumber}`}
                      >
                        <IconDownload size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
